import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { pagedQuickSearch } from '../services/globalSearchService';
import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from '../helper/constants';

const CACHE_TTL_MS = 30 * 1000;
const CACHE_MAX_ENTRIES = 10;

const pagedCache = new Map();

const cacheGet = (key) => {
    const entry = pagedCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        pagedCache.delete(key);
        return null;
    }
    pagedCache.delete(key);
    pagedCache.set(key, entry);
    return entry.value;
};

const cacheSet = (key, value) => {
    if (pagedCache.size >= CACHE_MAX_ENTRIES) {
        const oldestKey = pagedCache.keys().next().value;
        pagedCache.delete(oldestKey);
    }
    pagedCache.set(key, { value, timestamp: Date.now() });
};

export const clearPagedSearchCache = () => {
    pagedCache.clear();
};

const usePagedQuickSearch = (query, page, pageSize) => {
    const trimmed = (query ?? '').trim();
    const shouldSearch = trimmed.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

    const [state, setState] = useState({
        loading: false,
        error: null,
        results: [],
        totalCount: 0,
        totalPages: 0
    });

    const abortRef = useRef(null);
    const [refetchCounter, setRefetchCounter] = useState(0);
    const refetch = useCallback(() => setRefetchCounter((n) => n + 1), []);

    useEffect(() => {
        if (!shouldSearch) {
            if (abortRef.current) {
                abortRef.current.abort();
                abortRef.current = null;
            }
            setState({ loading: false, error: null, results: [], totalCount: 0, totalPages: 0 });
            return undefined;
        }

        const cacheKey = `${page}|${pageSize}|${trimmed.toLowerCase()}`;
        const cached = cacheGet(cacheKey);
        if (cached) {
            setState({ loading: false, error: null, ...cached });
            return undefined;
        }

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setState((prev) => ({ ...prev, loading: true, error: null }));

        pagedQuickSearch(trimmed, page, pageSize, { signal: controller.signal })
            .then((response) => {
                const payload = response.data?.data ?? response.data ?? {};
                const results = payload.results ?? [];
                const totalCount = payload.totalCount ?? 0;
                const totalPages = payload.totalPages ?? 0;
                const cacheValue = { results, totalCount, totalPages };
                cacheSet(cacheKey, cacheValue);
                setState({ loading: false, error: null, results, totalCount, totalPages });
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                setState({ loading: false, error, results: [], totalCount: 0, totalPages: 0 });
            });

        return () => controller.abort();
    }, [trimmed, shouldSearch, page, pageSize, refetchCounter]);

    return { ...state, refetch };
};

export default usePagedQuickSearch;
